/**
 * The group-chat post, generated instead of typed.
 *
 * Someone has been writing these out by hand every evening — standings, then
 * each nine's result with the tournament points in brackets, then the tiebreak
 * evidence where two men finished level. It is exactly the kind of transcription
 * the app already has the numbers for, and exactly the kind that goes wrong at
 * the end of a long day.
 *
 * The output deliberately copies the house style rather than inventing one. It
 * is going to be pasted straight into the thread next to five years of messages
 * that look like this.
 */

import { courseHandicapFor, findTee, holesFor, playingIndex } from './engine/handicap'
import { formatAmerican, impliedProbability } from './engine/odds'
import type { Match, Tournament, UnitTab } from './engine/types'
import type { TournamentView } from './select'
import { matchNameIn } from './engine/naming'
import { inPlayOrder, playerName } from './select'

/**
 * The quota number the group actually says out loud.
 *
 * The engine ranks on `delta` — points earned against the target — because that
 * is the number with a sign, and a sign is what tells you whether you beat your
 * quota. The scorecard and the group chat both use the other convention: you
 * start with a handicap-based bonus and add your hole points, so the numbers
 * come out in the twenties. They rank identically, since the two differ by a
 * constant, but only one of them matches the card in someone's back pocket.
 */
function quotaTotal(tab: UnitTab, match: Match): number | null {
  if (tab.detail.kind !== 'quota' || match.config.kind !== 'quota') return null
  const perPar = match.config.quotaFormula.pointsPerPar
  return tab.detail.delta + perPar * tab.perHole.length
}

/** "0 birdies, 4 pars" — only the counters that actually did the separating. */
function evidence(a: UnitTab, b: UnitTab, playerId: string): string {
  const mine = a.counters[playerId]
  if (!mine) return ''
  const theirs = Object.values(b.counters)[0]
  if (!theirs) return ''
  if (mine.eagles !== theirs.eagles) return `${mine.eagles} eagle${mine.eagles === 1 ? '' : 's'}`
  if (mine.birdies !== theirs.birdies)
    return `${mine.birdies} birdie${mine.birdies === 1 ? '' : 's'}`
  if (mine.pars !== theirs.pars) return `${mine.pars} par${mine.pars === 1 ? '' : 's'}`
  return `${mine.birdies} birdie${mine.birdies === 1 ? '' : 's'}, ${mine.pars} par${
    mine.pars === 1 ? '' : 's'
  }`
}

/** One match, ranked, with the tournament points each place earned in brackets. */
export function matchText(doc: Tournament, view: TournamentView, matchId: string): string {
  const entry = view.byMatch[matchId]
  if (!entry) return ''
  const { match, tabs, settlement } = entry

  const ranked = [...tabs].sort((a, b) => b.rankValue - a.rankValue)
  const lines = ranked.map((tab, i) => {
    const names = tab.playerIds.map((p) => playerName(doc, p)).join(' & ')
    const award = settlement.awards.find((a) => a.playerId === tab.playerIds[0])
    const points = award ? ` (${award.points})` : ''

    const quota = quotaTotal(tab, match)
    if (quota !== null) {
      // Show the evidence only where it did some work — a tie on the raw number.
      const tied = ranked.filter((t) => quotaTotal(t, match) === quota)
      const why =
        tied.length > 1 ? ` (${evidence(tab, tied.find((t) => t !== tab)!, tab.playerIds[0]!)})` : ''
      return `${i + 1}. ${names} – ${quota}${why}${points}`
    }
    return `${i + 1}. ${names} – ${tab.chip.primary}${points}`
  })

  return [matchNameIn(doc, match), ...lines].join('\n')
}

/** The running tournament table, points only — the thing people screenshot. */
export function standingsText(doc: Tournament, view: TournamentView, note?: string): string {
  const head = note
    ? `WOODSMEN CURRENT STANDINGS ${note}:`
    : 'WOODSMEN CURRENT STANDINGS:'
  const rows = view.standings.rows.map(
    (r, i) => `${i + 1}. ${playerName(doc, r.playerId)} – ${r.points}`,
  )
  return [head, '', ...rows].join('\n')
}

/** What each place is worth in a match, for the reminder people always ask for. */
export function payoutText(doc: Pick<Tournament, 'rounds' | 'courses'>, match: Match): string {
  const c = match.config
  if (c.kind !== 'quota') return ''
  const band = (from: number, to: number) =>
    from === to ? `${from}${from === 1 ? 'st' : from === 2 ? 'nd' : from === 3 ? 'rd' : 'th'}` : `${from}-${to}`
  return [
    `${matchNameIn(doc, match)} pays:`,
    ...c.payouts.map((p) => `${band(p.from, p.to)}: ${p.points} point${p.points === 1 ? '' : 's'}`),
  ].join('\n')
}

/**
 * The board, as a message.
 *
 * Favorite first, same order the app shows, because the shape of the field is
 * the message — "Tyler's the favorite and Phil's a long shot" is the whole
 * content of the post. Unpriced players are named at the end rather than left
 * out: somebody always asks about the man who is missing.
 *
 * Sorted on implied probability, not the raw number, for the same reason the
 * board is: -150 is shorter than +120 and +120 is shorter than +2000, so
 * sorting the printed figure would put the field in a nonsense order.
 */
export function oddsText(
  doc: Tournament,
  markets: { subject_id: string | null; american_odds: number; status: string; market: string }[],
): string {
  const open = markets.filter(
    (m) => m.market === 'outright' && m.subject_id && m.status === 'open',
  )
  const priced = new Map(open.map((m) => [m.subject_id!, m.american_odds]))

  const active = doc.players.filter((p) => p.active)
  const withLine = active
    .filter((p) => priced.has(p.id))
    .sort((a, b) => impliedProbability(priced.get(b.id)!) - impliedProbability(priced.get(a.id)!))
  const without = active.filter((p) => !priced.has(p.id))

  /*
   * No dash between the name and the price.
   *
   * "Tyler – -150" puts two dashes next to each other and the first one reads
   * as part of the number — the shortest price on the board looked like a typo.
   * A space is enough: the sign is doing the separating.
   */
  /*
   * Full names on the board, short names everywhere else.
   *
   * The leaderboard says "Mark" because the row is 60px wide and everybody in
   * the group knows which Mark. A price is different: it gets pasted into the
   * thread, screenshotted, and read back in December, and the house style has
   * always spelled a man out in full when money is against his name.
   */
  const rows = withLine.map(
    (p) => `${p.fullName || p.displayName} ${formatAmerican(priced.get(p.id)!)}`,
  )
  const tail = without.length
    ? ['', `No price yet: ${without.map((p) => p.fullName || p.displayName).join(', ')}`]
    : []

  // The line that makes it a book rather than a list of opinions.
  const terms = doc.settings?.venmoHandle
    ? ['', `*All bets are final once received on Venmo (${doc.settings.venmoHandle}).`]
    : ['', '*All bets are final once received.']

  // The group's own sign-off, verbatim from the post the book has always
  // gone out as. It is not decoration; it is the format.
  return [`${doc.name} - The Book`, '', ...rows, ...tail, ...terms, '', 'It’s officially mustache season!!'].join('\n')
}

/**
 * The roster with what everybody is getting.
 *
 * Index and course handicap together, because the question is never one or the
 * other — it is "what is he playing off". The course handicap is the number
 * that decides pops, and it is the one people query, so it goes last where the
 * eye lands.
 */
export function rosterText(doc: Tournament): string {
  const active = doc.players.filter((p) => p.active)

  /*
   * A course handicap needs a card, so one has to be chosen. The first round's
   * course is the honest pick: it is the one everyone is holding when this
   * message goes out, and quoting Thursday's course would be worse than none.
   */
  const firstRound = inPlayOrder(doc.rounds)[0]
  const course = firstRound
    ? doc.courses.find((c) => c.id === firstRound.courseId)
    : undefined
  // Eighteen holes. See the note in Field.tsx: this borrowed the first round's
  // nine and put a halved allowance into the message pasted to the group.
  const holes = course ? holesFor(course, 'full18') : []

  const chOf = (p: Tournament['players'][number]): number | null => {
    if (!course || holes.length === 0) return null
    // The same tee the app plays them off, so the number matches the card.
    return courseHandicapFor(playingIndex(p), findTee(course, p), holes)
  }

  const rows = active
    .slice()
    .sort((a, b) => playingIndex(a) - playingIndex(b))
    .map((p) => {
      const ch = chOf(p)
      /*
       * The low index rides along when it is known, and is silent when it is
       * not — it has to be looked up per man in GHIN, so a half-filled column
       * is the normal state and "low —" twelve times is noise.
       *
       * Named in the line rather than left as a bare second number, because
       * two decimals side by side invite the reading "8.4 to 6.1", which is
       * backwards: the low one came first.
       */
      /*
       * The number he is PLAYING off always comes first.
       *
       * That is the invariant worth keeping: whatever else the line says, the
       * figure straight after the dash is the one his strokes come from. This
       * used to print the current index unconditionally, which quietly lied
       * about every man set to play off his low.
       *
       * The other number rides along when it is known and is silent when it is
       * not — most of the field will never have been looked up in GHIN, and a
       * column of dashes is noise.
       */
      const hasLow = typeof p.lowIndex === 'number'
      const onLow = p.playsOff === 'low' && hasLow
      const head = onLow
        ? `low ${p.lowIndex!.toFixed(1)}, index ${p.handicapIndex.toFixed(1)}`
        : `${p.handicapIndex.toFixed(1)}${hasLow ? `, low ${p.lowIndex!.toFixed(1)}` : ''}`
      return ch == null
        ? `${p.displayName} – ${head}`
        : `${p.displayName} – ${head} (CH ${ch})`
    })

  const where = course ? ` (CH over 18 at ${course.name})` : ''
  return [
    `${doc.name.toUpperCase()} — ROSTER & INDEXES${where}:`,
    '',
    ...rows,
    '',
    `${active.length} players`,
  ].join('\n')
}
